import { cn } from '@repo/lib/cn';
import { useVirtualizer } from '@tanstack/react-virtual';
import { eachYearOfInterval, endOfYear, format, startOfYear } from 'date-fns';
import { ChevronDownIcon } from 'lucide-react';
import { useEffect, useRef, useState } from 'react';
import { type Matcher, type OnSelectHandler } from 'react-day-picker';

import { Button, ButtonGroup } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Drawer, DrawerBody, DrawerContent, DrawerDescription, DrawerHeader, DrawerTitle, DrawerTrigger } from '@/components/ui/drawer';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { useMediaQuery } from '@/hooks/media-query';

import type { CaptionLabelProps, DatePickerCalendarProps, DatePickerProps, MonthGridPropsExtended } from '.';

export function DatePicker(datePickerProps: DatePickerProps) {
  const { onSelect, selected, matcher, className, dateFormat, showIcon = true, children, ...props } = datePickerProps;
  const [open, setOpen] = useState<boolean>(false);
  const breakpoint: boolean = useMediaQuery('sm');

  const handleSelect = (date: Date) => {
    onSelect?.(date);
    setOpen(false);
  };

  if (breakpoint) {
    return (
      <Popover open={open} onOpenChange={setOpen} modal>
        <PopoverTrigger data-state={open ? 'open' : 'closed'} asChild>
          {children ? (
            children
          ) : (
            <Button variant={'outline'} className={cn('justify-start font-normal [&[data-state=open]>svg]:rotate-180', className)} {...props}>
              <span className={cn('truncate', !selected && 'text-muted-foreground')}>{selected ? format(selected, dateFormat ?? 'PP') : 'Wybierz datę'}</span>
              {showIcon ? <ChevronDownIcon aria-hidden="true" className="ml-auto" /> : null}
            </Button>
          )}
        </PopoverTrigger>
        <PopoverContent className="w-auto border-0 p-0" align="start">
          <DatePickerCalendar selected={selected} onSelect={handleSelect} matcher={matcher} />
        </PopoverContent>
      </Popover>
    );
  }

  return (
    <Drawer open={open} onOpenChange={setOpen}>
      <DrawerTrigger asChild data-state={open ? 'open' : 'closed'}>
        {children ? (
          children
        ) : (
          <Button variant={'outline'} className={cn('justify-start font-normal [&[data-state=open]>svg]:rotate-180', className)} {...props}>
            <span className={cn('truncate', !selected && 'text-muted-foreground')}>{selected ? format(selected, dateFormat ?? 'PP') : 'Wybierz datę'}</span>
            {showIcon ? <ChevronDownIcon aria-hidden="true" className="ml-auto" /> : null}
          </Button>
        )}
      </DrawerTrigger>
      <DrawerContent>
        <DrawerBody>
          <DrawerHeader>
            <DrawerTitle className="sr-only">Wybierz datę</DrawerTitle>
            <DrawerDescription className="sr-only">Kliknij wybraną datę, aby ją zaznaczyć</DrawerDescription>
          </DrawerHeader>
          <DatePickerCalendar selected={selected} onSelect={handleSelect} matcher={matcher} className="mx-auto w-full border-none" />
        </DrawerBody>
      </DrawerContent>
    </Drawer>
  );
}

export function DatePickerCalendar(datePickerCalendar: DatePickerCalendarProps) {
  const { onSelect, selected, matcher, className } = datePickerCalendar;
  const today = new Date();

  const [month, setMonth] = useState<Date>(selected || today);
  const [isYearView, setIsYearView] = useState<boolean>(false);
  const [isMonthView, setIsMonthView] = useState<boolean>(false);

  const { startDate, endDate } = getAllowedDateRange(matcher);

  const years = eachYearOfInterval({
    start: startOfYear(startDate),
    end: endOfYear(endDate),
  });

  const handleSelect: OnSelectHandler<Date> = (selectedDate: Date) => {
    onSelect?.(selectedDate);
  };

  return (
    <Calendar
      mode="single"
      required={true}
      selected={selected ?? undefined}
      onSelect={handleSelect}
      month={month}
      onMonthChange={setMonth}
      startMonth={startDate}
      endMonth={endDate}
      disabled={matcher}
      className={cn('overflow-hidden rounded-md border p-2', className)}
      classNames={{
        month_caption: 'flex flex-col w-full mx-auto max-w-2xs px-11',
        nav: 'flex gap-1 absolute top-0 left-0 w-full right-0 justify-between mx-auto max-w-sm',
      }}
      components={{
        CaptionLabel: (props) => <CaptionLabel isYearView={isYearView} setIsYearView={setIsYearView} isMonthView={isMonthView} setIsMonthView={setIsMonthView} {...props} />,
        MonthGrid: (props) => {
          return (
            <MonthGrid
              isYearView={isYearView}
              isMonthView={isMonthView}
              setIsYearView={setIsYearView}
              setIsMonthView={setIsMonthView}
              startDate={startDate}
              endDate={endDate}
              years={years}
              month={month}
              onMonthSelect={(selectedMonth: Date) => {
                setMonth(selectedMonth);
                setIsYearView(false);
                setIsMonthView(false);
              }}
              {...props}
            />
          );
        },
      }}
    />
  );
}

export function MonthGrid(monthGridProps: MonthGridPropsExtended) {
  const { className, children, isYearView, isMonthView, startDate, endDate, years, month, onMonthSelect } = monthGridProps;

  const currentMonth: number = month.getMonth();
  const currentYear: number = month.getFullYear();

  const currentYearButtonRef = useRef<HTMLButtonElement>(null);
  const currentMonthButtonRef = useRef<HTMLButtonElement>(null);
  const parentRef = useRef<HTMLDivElement>(null);

  const rowVirtualizer = useVirtualizer({
    count: years.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 40,
    overscan: 6,
  });

  const handleYearSelect = (year: number) => {
    onMonthSelect(new Date(year, currentMonth));
  };

  const handleMonthSelect = (month: number) => {
    onMonthSelect(new Date(currentYear, month));
  };

  useEffect(() => {
    if (isYearView && parentRef.current) {
      const yearButton = currentYearButtonRef.current;
      const startYear = startDate.getFullYear();
      rowVirtualizer.scrollToIndex(currentYear - startYear, {
        align: 'center',
      });
      setTimeout(() => {
        yearButton?.focus();
      }, 100);
    }
  }, [isYearView]);

  useEffect(() => {
    if (isMonthView) {
      const monthButton = currentMonthButtonRef.current;
      monthButton?.scrollIntoView({
        block: 'center',
        behavior: 'instant',
      });
      setTimeout(() => {
        monthButton?.focus();
      }, 100);
    }
  }, [isMonthView, currentMonth]);

  return (
    <div className="relative">
      <table className={cn('mx-auto', className)}>{children}</table>
      {isYearView && (
        <div
          ref={parentRef}
          className="bg-popover absolute inset-0 z-20 -mx-2 -mb-2 flex p-2"
          style={{
            overflow: 'auto',
          }}
        >
          <div
            style={{
              height: `${rowVirtualizer.getTotalSize()}px`,
              width: '100%',
              position: 'relative',
            }}
          >
            {rowVirtualizer.getVirtualItems().map((virtualRow) => {
              const year: Date | undefined = years[virtualRow.index];
              const isCurrentYear: boolean = year?.getFullYear() === currentYear;
              return (
                <Button
                  type={'button'}
                  key={virtualRow.index}
                  ref={isCurrentYear ? currentYearButtonRef : undefined}
                  variant={isCurrentYear ? 'tertiary' : 'ghost'}
                  style={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: '100%',
                    height: `${virtualRow.size}px`,
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                  onClick={() => (year ? handleYearSelect(year.getFullYear()) : null)}
                >
                  {year?.getFullYear().toString() ?? ''}
                </Button>
              );
            })}
          </div>
        </div>
      )}
      {isMonthView && (
        <div
          ref={parentRef}
          className="bg-popover absolute inset-0 z-20 -mx-2 -mb-2 flex flex-col p-2"
          style={{
            overflow: 'auto',
          }}
        >
          {Array.from({ length: 12 }).map((_, idx) => {
            const monthDate = new Date(currentYear, idx);
            const monthName: string = format(monthDate, 'LLLL');
            const isCurrentMonth: boolean = idx === currentMonth;
            const isDisabled: boolean = monthDate < startDate || monthDate > endDate;

            return (
              <Button
                type={'button'}
                key={idx}
                ref={isCurrentMonth ? currentMonthButtonRef : undefined}
                variant={isCurrentMonth ? 'tertiary' : 'ghost'}
                className="w-full"
                onClick={() => handleMonthSelect(idx)}
                disabled={isDisabled}
              >
                {monthName}
              </Button>
            );
          })}
        </div>
      )}
    </div>
  );
}

export function CaptionLabel(captionLabelProps: CaptionLabelProps) {
  const { children, isYearView, setIsYearView, isMonthView, setIsMonthView } = captionLabelProps;
  const [month, year] = typeof children === 'string' ? children.split(' ') : ['', ''];

  const handleMonthViewOpen = () => {
    setIsYearView(false);
    setIsMonthView((prev) => !prev);
  };
  const handleYearViewOpen = () => {
    setIsMonthView(false);
    setIsYearView((prev) => !prev);
  };

  return (
    <ButtonGroup className="w-full">
      <Button
        variant="ghost"
        size="sm"
        type="button"
        onClick={handleMonthViewOpen}
        data-state={isMonthView ? 'open' : 'closed'}
        className="data-[state=open]:text-accent-foreground flex w-full items-center gap-1 text-sm font-medium [&[data-state=open]>svg]:rotate-180"
      >
        {month}
        <ChevronDownIcon />
      </Button>
      <Button
        variant="ghost"
        size="sm"
        type="button"
        onClick={handleYearViewOpen}
        data-state={isYearView ? 'open' : 'closed'}
        className="data-[state=open]:text-accent-foreground flex w-full items-center gap-1 text-sm font-medium [&[data-state=open]>svg]:rotate-180"
      >
        {year}
        <ChevronDownIcon />
      </Button>
    </ButtonGroup>
  );
}
export function getAllowedDateRange(disabled?: Matcher | Matcher[]) {
  const today = new Date();
  let minDate: Date = new Date(today.getFullYear() - 150, 0, 1);
  let maxDate: Date = new Date(today.getFullYear() + 150, 11, 31);

  const matchers: Matcher[] = Array.isArray(disabled) ? disabled : disabled ? [disabled] : [];

  for (const matcher of matchers) {
    if (typeof matcher !== 'object') continue;

    if ('before' in matcher && matcher.before instanceof Date) {
      const d = new Date(matcher.before);
      if (d > minDate) minDate = d;
    }

    if ('after' in matcher && matcher.after instanceof Date) {
      const d = new Date(matcher.after);
      if (d < maxDate) maxDate = d;
    }
  }

  if (minDate > maxDate) {
    const d = minDate;
    minDate = new Date(d.getFullYear(), d.getMonth(), 1);
    maxDate = new Date(d.getFullYear(), d.getMonth(), 1);
  }

  return { startDate: minDate, endDate: maxDate };
}
